import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, map, Subject, takeUntil } from 'rxjs';

import { TriggerAction } from '@shared/actions';
import { StateManagerProjectService } from '@state-manager';
import { IViewersObjectBoolean } from '@viewer/cornerstone.data';
import { CornerstoneService } from '@viewer/cornerstone.service';
import { BaseToolComponent } from '../base-tool/base-tool.component';
import { KeyInteractionService } from 'src/app/interaction';
import { ConfirmDialogService } from '@shared/confirm-dialog';
import {
  HotKey,
  HotKeySettings,
  ViewChangesToolComponentKeyActionName,
  ViewChangesToolComponentKeyActionNameList,
} from '@viewer/data';
import { ConfirmDialogAnswer, ConfirmDialogResults } from '@shared/confirm-dialog/confirm-dialog.data';

@Component({
  selector: 'app-view-changes-tool',
  templateUrl: 'view-changes-tool.component.html',
  styleUrls: ['view-changes-tool.component.scss'],
})
export class ViewChangesToolComponent
  extends BaseToolComponent
  implements OnInit, OnDestroy
{
  @Input() useHotKeys = true;

  viewChangesToolAction!: TriggerAction;
  forceHighlight: boolean = false;

  private _unsubcribe$ = new Subject<void>();

  constructor(
    public stateProjectManagerService: StateManagerProjectService,
    private _cornerstoneService: CornerstoneService,
    private _confirmDialogService: ConfirmDialogService,
    private _keyInteractionService: KeyInteractionService
  ) {
    super(stateProjectManagerService);
  }

  ngOnInit(): void {
    this.viewChangesToolAction = new TriggerAction(
      'view-changes',
      'View changes',
      (_) => {
        if(!this.forceHighlight) {
         this.showWarningModal();
        } else {
         this.showInfoModal()
        }
      }
    );

    this.viewChangesToolAction.isEnable = () =>
      combineLatest([
        this.isDisabled$.pipe(map((x) => !x)),
        this.stateProjectManagerService.isProcessOfShowDiffHide$,
      ]).pipe(
        map(([isEnabled, isProcessOfShowDiffHide]) => {
          return (
            isEnabled &&
            !isProcessOfShowDiffHide[
              `viewer${this.stateProjectManagerService.currentSelectedWindow}`
            ]
          );
        })
      );

    this.stateProjectManagerService.isActiveShowChangesMode$
      .pipe(takeUntil(this._unsubcribe$))
      .subscribe((value: boolean) => {
        this.forceHighlight = value;
      });

    if (this.useHotKeys) {
      this._initializeHotKeys();
    }
  }

  ngOnDestroy(): void {
    this._unsubcribe$.next();
    this._unsubcribe$.complete();

    if (this.useHotKeys) {
      this._keyInteractionService.removeKeySettingsList([
        ...ViewChangesToolComponentKeyActionNameList,
      ]);
    }
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.C,
        new HotKeySettings<ViewChangesToolComponentKeyActionName>({
          keyActionName: 'ViewChangesActivate',
        })
      )
      .pipe(takeUntil(this._unsubcribe$))
      .subscribe(() => {
        this.viewChangesToolAction.trigger();
      });
  }

  showWarningModal() {
    this._confirmDialogService
    .openConfirmDialog(
      'Dialog.VIEW_CHANGES_MESSAGE',
      'Dialog.RejectButtons.CANCEL',
      'Dialog.ConfirmButtons.CONTINUE'
    )
    .pipe(takeUntil(this._unsubcribe$))
    .subscribe((res: ConfirmDialogAnswer) => {
      if (res.result === ConfirmDialogResults[1]) {
        this._cornerstoneService.activateShowDiff();
      } else {
        return;
      }
    });
  }

  showInfoModal() {
    this._confirmDialogService
    .openConfirmDialog(
      'Dialog.VIEW_CHANGES_CANCEL_MESSAGE',
      'Dialog.ConfirmButtons.OK'
    )
    .pipe(takeUntil(this._unsubcribe$))
    .subscribe((res) => {
        this._cornerstoneService.cancelShowDiff();
    });
  }
}
